import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Gamepad2, Play, Trophy, Clock, Target, X, Check,
  Globe, Loader2, RotateCcw, Medal
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Import centralized language utilities
import { getCountryCodeFromString } from '../utils/languageUtils';

const getCountryCode = getCountryCodeFromString;

const FlagImage = ({ countryCode, className = "w-5 h-4" }) => {
  if (!countryCode) return <Globe className={`${className} text-muted-foreground`} />;
  return (
    <img 
      src={`https://flagcdn.com/w40/${countryCode}.png`}
      alt={countryCode}
      className={`${className} object-cover rounded`}
      loading="lazy"
    />
  );
};

const getCategoryLabel = (key, t) => {
  const labels = {
    new: t('wordHunt.newWords'),
    learning: t('wordHunt.learning'),
    familiar: t('wordHunt.familiar'),
    known: t('wordHunt.known'),
  };
  return labels[key] || key;
};

const categoryColors = {
  new: 'bg-red-500',
  learning: 'bg-yellow-500',
  familiar: 'bg-green-500',
  known: 'bg-amber-400',
};

export default function Game() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Game state
  const [gameActive, setGameActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [words, setWords] = useState([]);
  const [clickedIndices, setClickedIndices] = useState(new Set());
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showTrophy, setShowTrophy] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Scoreboard
  const [scores, setScores] = useState([]);
  const [showScoreboard, setShowScoreboard] = useState(false);

  // Handle resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch user's languages
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/languages/user/me`, { withCredentials: true });
        setLanguages(res.data || []);
        if (res.data?.length > 0) {
          setSelectedLanguage(res.data[0].language_id);
        }
      } catch (err) {
        console.error('Error fetching languages:', err);
      }
    };
    fetchLanguages();
  }, []);

  // Fetch categories when language changes
  useEffect(() => {
    if (!selectedLanguage) return;
    
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/game/categories/${selectedLanguage}`,
          { withCredentials: true }
        );
        setCategories(res.data.categories || []);
        // Auto-select first available category
        const available = res.data.categories?.find(c => c.available);
        if (available) {
          setSelectedCategory(available.name);
        } else {
          setSelectedCategory('');
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, [selectedLanguage]);

  // Timer
  useEffect(() => {
    let interval;
    if (gameActive && startTime && !gameOver) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameActive, startTime, gameOver]);

  // Check win/lose conditions
  useEffect(() => {
    if (!gameActive) return;
    
    // Win: found all 10 target words
    if (correctCount >= 10) {
      handleGameEnd(true);
    }
    // Lose: 15 attempts used
    else if (attempts >= 15) {
      handleGameEnd(false);
    }
  }, [correctCount, attempts, gameActive]);

  const handleGameEnd = async (won) => {
    setGameOver(true);
    setGameActive(false);
    
    if (won) {
      setShowTrophy(true);
    }
    
    // Calculate score: correct - wrong - (seconds/100), rounded to 1 decimal
    const finalScore = Math.round((correctCount - wrongCount - (elapsedTime / 100)) * 10) / 10;
    
    // Save score
    try {
      const langName = languages.find(l => l.language_id === selectedLanguage)?.language?.name || languages.find(l => l.language_id === selectedLanguage)?.name || 'Unknown';
      await axios.post(
        `${BACKEND_URL}/api/game/score`,
        {
          language_id: selectedLanguage,
          language_name: langName,
          category: selectedCategory,
          score: finalScore,
          correct: correctCount,
          wrong: wrongCount,
          time_seconds: elapsedTime
        },
        { withCredentials: true }
      );
      toast.success(`Score saved: ${finalScore}`);
      fetchScores();
    } catch (err) {
      console.error('Error saving score:', err);
    }
  };

  const fetchScores = async () => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/game/scores?limit=20`,
        { withCredentials: true }
      );
      setScores(res.data.scores || []);
    } catch (err) {
      console.error('Error fetching scores:', err);
    }
  };

  useEffect(() => {
    fetchScores();
  }, []);

  const startGame = async () => {
    if (!selectedLanguage || !selectedCategory) {
      toast.error('Select a language and category first');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/game/words`,
        { language_id: selectedLanguage, category: selectedCategory },
        { withCredentials: true }
      );
      
      setWords(res.data.words);
      setClickedIndices(new Set());
      setCorrectCount(0);
      setWrongCount(0);
      setAttempts(0);
      setElapsedTime(0);
      setStartTime(Date.now());
      setGameActive(true);
      setGameOver(false);
      setShowTrophy(false);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to start game';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleWordClick = useCallback((index) => {
    if (!gameActive || gameOver || clickedIndices.has(index)) return;
    
    const word = words[index];
    const newClicked = new Set(clickedIndices);
    newClicked.add(index);
    setClickedIndices(newClicked);
    setAttempts(prev => prev + 1);
    
    if (word.is_target) {
      setCorrectCount(prev => prev + 1);
    } else {
      setWrongCount(prev => prev + 1);
    }
  }, [gameActive, gameOver, clickedIndices, words]);

  const resetGame = () => {
    setGameActive(false);
    setGameOver(false);
    setWords([]);
    setClickedIndices(new Set());
    setCorrectCount(0);
    setWrongCount(0);
    setAttempts(0);
    setElapsedTime(0);
    setStartTime(null);
    setShowTrophy(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const currentScore = Math.round((correctCount - wrongCount - (elapsedTime / 100)) * 10) / 10;

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto" data-testid="game-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-500/10">
            <Gamepad2 className="h-6 w-6 text-purple-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{t('wordHunt.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('wordHunt.subtitle')}</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowScoreboard(!showScoreboard)}
          data-testid="toggle-scoreboard"
        >
          <Medal className="h-4 w-4 mr-2" />
          {showScoreboard ? t('wordHunt.hideScores') : t('wordHunt.scoreboard')}
        </Button>
      </div>

      {/* Controls */}
      {!gameActive && !gameOver && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              {/* Language Select */}
              <div className="flex-1 w-full">
                <label className="text-sm font-medium mb-2 block">{t('wordHunt.language')}</label>
                <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                  <SelectTrigger data-testid="language-select">
                    <SelectValue placeholder={t('wordHunt.selectLanguage')} />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map(lang => (
                      <SelectItem key={lang.language_id} value={lang.language_id}>
                        <div className="flex items-center gap-2">
                          <FlagImage countryCode={getCountryCode(lang.language?.code || lang.code)} className="w-5 h-4" />
                          <span>{lang.language?.name || lang.name || 'Unknown'}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Category Select */}
              <div className="flex-1 w-full">
                <label className="text-sm font-medium mb-2 block">{t('wordHunt.category')}</label>
                <Select 
                  value={selectedCategory} 
                  onValueChange={setSelectedCategory}
                  disabled={loadingCategories || categories.length === 0}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder={loadingCategories ? t('common.loading') : t('wordHunt.selectCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem 
                        key={cat.name} 
                        value={cat.name}
                        disabled={!cat.available}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${categoryColors[cat.name]}`} />
                          {getCategoryLabel(cat.name, t)} ({cat.count} {t('common.words')})
                          {!cat.available && <span className="text-xs text-muted-foreground ml-1">(need 10+)</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* GO Button */}
              <Button
                onClick={startGame}
                disabled={loading || !selectedLanguage || !selectedCategory}
                className="h-10 px-8 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                data-testid="start-game-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    {t('wordHunt.go')}
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-muted-foreground mt-4">
              {t('wordHunt.findWords', { 
                target: isMobile ? 8 : 10, 
                category: getCategoryLabel(selectedCategory, t) || t('wordHunt.category'),
                total: isMobile ? 64 : 100,
                attempts: 15
              })}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Game Stats Bar */}
      {(gameActive || gameOver) && (
        <div className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-lg bg-card border">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              <span className="font-bold text-green-500">{correctCount}/10</span>
            </div>
            <div className="flex items-center gap-2">
              <X className="h-5 w-5 text-red-500" />
              <span className="font-bold text-red-500">{wrongCount}</span>
            </div>
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">{attempts}/15 {t('wordHunt.attempts')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-muted-foreground" />
              <span className="font-mono">{formatTime(elapsedTime)}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <span className="text-sm text-muted-foreground">{t('wordHunt.score')}: </span>
              <span className={`font-bold text-lg ${currentScore >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {currentScore}
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={resetGame}>
              <RotateCcw className="h-4 w-4 mr-2" />
              {t('common.reset')}
            </Button>
          </div>
        </div>
      )}

      {/* Trophy Overlay */}
      {showTrophy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowTrophy(false)}>
          <div className="text-center animate-bounce">
            <Trophy className="h-32 w-32 text-yellow-400 mx-auto drop-shadow-lg" />
            <h2 className="text-4xl font-bold text-white mt-4">{t('wordHunt.youWon')}</h2>
            <p className="text-xl text-white/80 mt-2">{t('wordHunt.score')}: {currentScore}</p>
            <p className="text-sm text-white/60 mt-1">{t('common.continue')}</p>
          </div>
        </div>
      )}

      {/* Game Grid */}
      {words.length > 0 && (
        <div 
          className="grid gap-0.5 md:gap-1"
          style={{ 
            gridTemplateColumns: isMobile ? 'repeat(8, 1fr)' : 'repeat(10, 1fr)',
          }}
          data-testid="word-grid"
        >
          {/* On mobile, show first 64 words (8x8 grid), on desktop show all 100 (10x10) */}
          {(isMobile ? words.slice(0, 64) : words).map((word, index) => {
            const isClicked = clickedIndices.has(index);
            const isCorrect = isClicked && word.is_target;
            const isWrong = isClicked && !word.is_target;
            const displayText = isClicked ? word.translation : word.word;
            
            // Calculate font size based on word length and device
            const getFontSize = () => {
              const len = displayText.length;
              if (isMobile) {
                if (len > 10) return '0.55rem';
                if (len > 7) return '0.65rem';
                if (len > 5) return '0.75rem';
                return '0.85rem';
              }
              if (len > 12) return '0.6rem';
              if (len > 8) return '0.7rem';
              if (len > 5) return '0.8rem';
              return '0.9rem';
            };
            
            return (
              <button
                key={index}
                onClick={() => handleWordClick(index)}
                disabled={isClicked || gameOver}
                className={`
                  relative rounded font-semibold transition-all
                  flex items-center justify-center text-center
                  ${isCorrect ? 'bg-green-500 text-white' : ''}
                  ${isWrong ? 'bg-red-500 text-white' : ''}
                  ${!isClicked ? 'bg-card border hover:bg-muted hover:scale-105 cursor-pointer' : ''}
                  ${(gameOver && !isClicked && word.is_target) ? 'ring-2 ring-yellow-400' : ''}
                  aspect-square
                `}
                style={{ 
                  padding: isMobile ? '1px' : '2px',
                  fontSize: getFontSize(),
                  lineHeight: 1.1,
                  wordBreak: 'break-word',
                  hyphens: 'auto',
                  minHeight: isMobile ? '36px' : undefined,
                }}
                data-testid={`word-btn-${index}`}
              >
                <span className="w-full h-full flex items-center justify-center p-0.5 overflow-hidden" style={{ wordBreak: 'break-word' }}>
                  {displayText}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Game Over Message */}
      {gameOver && !showTrophy && (
        <Card className="border-2 border-dashed">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">
              {correctCount >= 10 ? `🎉 ${t('wordHunt.congratulations')}` : `⏱️ ${t('wordHunt.timeAttemptsUp')}`}
            </h3>
            <p className="text-muted-foreground mb-4">
              {t('wordHunt.foundWords', { count: correctCount, wrong: wrongCount, time: formatTime(elapsedTime) })}
            </p>
            <div className="flex justify-center gap-4">
              <Button onClick={startGame} className="gap-2">
                <Play className="h-4 w-4" />
                {t('common.playAgain')}
              </Button>
              <Button variant="outline" onClick={resetGame}>
                {t('common.newGame')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scoreboard */}
      {showScoreboard && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Medal className="h-5 w-5 text-yellow-500" />
              {t('wordHunt.yourScores')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {scores.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">{t('wordHunt.noScores')}</p>
            ) : (
              <ScrollArea className="h-64">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-medium">{t('wordHunt.date')}</th>
                      <th className="text-left py-2 font-medium">{t('wordHunt.language')}</th>
                      <th className="text-left py-2 font-medium">{t('wordHunt.category')}</th>
                      <th className="text-center py-2 font-medium">✓</th>
                      <th className="text-center py-2 font-medium">✗</th>
                      <th className="text-center py-2 font-medium">{t('wordHunt.time')}</th>
                      <th className="text-right py-2 font-medium">{t('wordHunt.score')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scores.map((score, idx) => (
                      <tr key={score.score_id || idx} className="border-b border-border/50">
                        <td className="py-2 text-muted-foreground">{formatDate(score.created_at)}</td>
                        <td className="py-2">{score.language_name}</td>
                        <td className="py-2">
                          <Badge variant="secondary" className={`${categoryColors[score.category]} text-white text-xs`}>
                            {getCategoryLabel(score.category, t)}
                          </Badge>
                        </td>
                        <td className="py-2 text-center text-green-500">{score.correct}</td>
                        <td className="py-2 text-center text-red-500">{score.wrong}</td>
                        <td className="py-2 text-center font-mono">{formatTime(Math.round(score.time_seconds))}</td>
                        <td className={`py-2 text-right font-bold ${score.score >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {score.score}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
